import { X } from 'lucide-react'
import { useCallback, useEffect, useRef, type RefObject, useState } from 'react'
import useOnclickOutside from 'react-cool-onclickoutside'
import { type MapRef, type ViewState } from 'react-map-gl'
import usePlacesAutocomplete, { getGeocode } from 'use-places-autocomplete'
import { PLOT_ZOOM_THRESHOLD } from '#app/consts'
import {
	checkPlacesRateLimit,
	recordPlacesRequest,
	PLACES_RATE_LIMITS,
} from '#app/utils/places-rate-limit'
import { Button } from '../ui/button'
import { Command, CommandInput, CommandItem, CommandList } from '../ui/command'

export const PlacesSearch = ({
	mapRef,
	viewState,
}: {
	mapRef?: RefObject<MapRef>
	viewState: ViewState
}) => {
	const {
		init,
		ready,
		value,
		suggestions: { status, data: suggestions },
		setValue,
		clearSuggestions,
	} = usePlacesAutocomplete({
		initOnMount: false,
		debounce: 300,
	})

	const [hideSuggestions, setHideSuggestions] = useState(false)
	const [rateLimitMessage, setRateLimitMessage] = useState<string | null>(null)

	const lastRecordedValue = useRef<string>('')
	const searchTimeoutRef = useRef<ReturnType<typeof setTimeout> | null>(null)
	const currentInputValue = useRef<string>('')

	// Initialize Places library dynamically
	useEffect(() => {
		let active = true
		const loadPlaces = async () => {
			try {
				if (
					typeof window !== 'undefined' &&
					typeof window.google?.maps?.importLibrary === 'function'
				) {
					await google.maps.importLibrary('places')
					if (active) {
						init()
					}
				} else {
					// Poll for importLibrary existence in case of slow script execution
					let attempts = 0
					const interval = setInterval(async () => {
						attempts++
						if (
							typeof window !== 'undefined' &&
							typeof window.google?.maps?.importLibrary === 'function'
						) {
							clearInterval(interval)
							await google.maps.importLibrary('places')
							if (active) {
								init()
							}
						} else if (attempts > 50) {
							clearInterval(interval)
						}
					}, 100)
				}
			} catch (err) {
				console.error('Failed to initialize Google Maps Places library:', err)
			}
		}

		loadPlaces()
		return () => {
			active = false
		}
	}, [init])

	// Debounced search handler - waits for user to stop typing and respects rate limits
	const handleInput = useCallback(
		(newValue: string) => {
			currentInputValue.current = newValue
			setValue(newValue, false)

			// Clear rate limit message when user is typing
			setRateLimitMessage(null)

			// Clear any pending search timeout
			if (searchTimeoutRef.current) {
				clearTimeout(searchTimeoutRef.current)
				searchTimeoutRef.current = null
			}

			// If value is too short or being cleared, clear suggestions and don't search
			if (newValue.length < PLACES_RATE_LIMITS.MIN_SEARCH_LENGTH) {
				clearSuggestions()
				return
			}

			// Debounce: wait for user to stop typing before checking rate limits and searching
			searchTimeoutRef.current = setTimeout(() => {
				// Double-check the value hasn't changed during debounce
				if (newValue !== currentInputValue.current) {
					searchTimeoutRef.current = null
					return
				}

				// Check rate limits only after user stops typing
				const rateLimitResult = checkPlacesRateLimit(newValue)

				if (!rateLimitResult.allowed) {
					if (rateLimitResult.reason === 'rate_limit') {
						setRateLimitMessage('Too many searches. Please wait a moment.')
					} else if (rateLimitResult.reason === 'daily_quota') {
						setRateLimitMessage(
							'Daily search limit reached. Try again tomorrow.',
						)
					}
					searchTimeoutRef.current = null
					return
				}

				// Only record and search if this is a different value than last recorded
				if (newValue !== lastRecordedValue.current) {
					recordPlacesRequest()
					lastRecordedValue.current = newValue
					setValue(newValue, true)
				}
				searchTimeoutRef.current = null
			}, 500)
		},
		[setValue, clearSuggestions, ready, value],
	)

	// Cleanup timeout on unmount
	useEffect(() => {
		return () => {
			if (searchTimeoutRef.current) {
				clearTimeout(searchTimeoutRef.current)
			}
		}
	}, [])

	const ref = useOnclickOutside(() => setHideSuggestions(true))

	const handleSelect =
		(suggestion: google.maps.places.AutocompletePrediction) => () => {
			const description = suggestion.description
			setValue(description, false)
			setHideSuggestions(true)
			lastRecordedValue.current = description

			getGeocode({ address: description })
				.then(results => {
					const bounds = results[0].geometry.bounds

					if (!mapRef?.current) return

					if (bounds) {
						mapRef.current.fitBounds([
							[bounds.getSouthWest().lng(), bounds.getSouthWest().lat()],
							[bounds.getNorthEast().lng(), bounds.getNorthEast().lat()],
						])
						return
					}

					const { lat, lng } = results[0].geometry.location
					mapRef.current.flyTo({
						center: [lng(), lat()],
						zoom: PLOT_ZOOM_THRESHOLD,
					})
				})
				.catch(err => {
					console.error('Geocoding error:', err)
				})
		}

	const showList = status === 'OK' && !hideSuggestions && suggestions.length > 0

	return (
		<div
			className="map-search absolute left-4 right-4 top-4 z-10 rounded-xl shadow md:left-auto"
			ref={ref}
		>
			<Command shouldFilter={false}>
				<CommandInput
					value={value}
					onValueChange={handleInput}
					disabled={!ready}
					placeholder={ready ? 'e.g. London' : 'Loading Google Maps...'}
					onFocus={() => setHideSuggestions(false)}
					className="h-10 pr-6 "
				/>
				{value && (
					<Button
						className="absolute right-3 top-3 h-4 w-4"
						variant="link"
						onClick={() => {
							setValue('')
							setHideSuggestions(true)
							setRateLimitMessage(null)
						}}
					>
						<X className="h-4 w-4" />
					</Button>
				)}

				{rateLimitMessage && (
					<div className="border-t bg-amber-50 px-3 py-2 text-sm text-amber-600">
						{rateLimitMessage}
					</div>
				)}

				{showList && (
					<CommandList>
						{suggestions.map((suggestion, index) => {
							const {
								place_id,
								structured_formatting: { main_text, secondary_text },
							} = suggestion

							return (
								<CommandItem
									key={`${place_id}-${index}`}
									onSelect={handleSelect(suggestion)}
								>
									<div className="flex flex-col">
										<strong>{main_text}</strong>
										<small>{secondary_text}</small>
									</div>
								</CommandItem>
							)
						})}
					</CommandList>
				)}
			</Command>
		</div>
	)
}
